import { Fragment } from 'react'
/** @jsx jsx */
import { useState, useContext } from 'react'
import { css, jsx } from '@emotion/core'
import uniqid from 'uniqid'
import { Button } from '@livechat/design-system'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import useMedia from 'use-media'

import { Context } from '../../context'
import { Field, NextField, SubmitModal } from '../../components'

const mainCss = css`
  width: calc(100vw - 200px);
  height: calc(100vh - 300px);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 50px;
  overflow-y: scroll;
  padding-bottom: 200px;

  @media (max-width: 855px) {
    width: 100vw;
    height: calc(100vh - 300px);
  }

  .field {
    margin-bottom: 20px;
  }

  .lc-modal-base {
    cursor: default !important;
    min-width: 300px !important;
  }
`

const buttonsCss = css`
  width: 600px;
  display: flex;
  justify-content: space-between;

  @media (max-width: 640px) {
    width: 94vw;
  }

  .save {
    width: 240px;

    > img {
      margin-right: 5px;
    }
  }
`

const Sandbox = () => {
  const {
    sandboxSurvey: survey,
    setSandboxSurvey: setSurvey,
    isTemplate,
  } = useContext(Context)

  const isSmall = useMedia({ maxWidth: 640 })
  const [isOpen, setIsOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const emptyField = {
    id: uniqid(),
    type: undefined,
    question: '',
    answers: [],
    required: true,
    shine: true,
    score: undefined,
  }

  const checkChanges = () => {
    const firstField = survey[0]
    if (firstField.question === '' || !firstField.type) {
      return false
    }

    return true
  }

  const addField = () => setSurvey([...survey, emptyField])
  const dropField = (id) => setSurvey([...survey].filter((el) => el.id !== id))
  const copyField = (id) => {
    const { type, question, answers, required } = survey.find(
      (field) => field.id === id
    )

    const newField = {
      id: uniqid(),
      type,
      question,
      answers,
      required,
      shine: true,
    }

    let isBefore = true
    const questionsBefore = []
    const questionsAfter = []
    ;[...survey].forEach((el) => {
      isBefore ? questionsBefore.push(el) : questionsAfter.push(el)

      if (el.id === id) {
        isBefore = false
      }
    })

    setSurvey([...questionsBefore, newField, ...questionsAfter])
  }

  const update = (id) => (key, value) => {
    const newSurvey = [...survey]
    newSurvey.forEach((el) => {
      if (el.id === id) {
        el[key] = value
      }
    })
    setSurvey(newSurvey)
  }

  const hasChanges = survey.length > 1 || checkChanges()

  const checkSurveyInvalid = () => {
    return survey.some((el) => {
      if (!el.question || !el.type) {
        return true
      }
      if (el.question.length > 200) {
        return true
      }
      if (el.type === 'SINGLE_CHOICE' || el.type === 'SINGLE_CHOICE') {
        if (!el.answers || el.answers.length < 1) {
          return true
        }
      }
      if (el.type === 'SCORE') {
        const { range, labels } = el.score
        if (!range?.min || !range?.max) {
          return true
        }

        if (isNaN(range?.min) || isNaN(range?.max)) {
          return true
        }

        if (!labels?.min || !labels?.max) {
          return true
        }

        if (labels?.max === labels?.min) {
          return true
        }
      }

      return false
    })
  }

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const mySurvey = [...survey]
    mySurvey.forEach((el) => (el.shine = false))

    const items = reorder(
      mySurvey,
      result.source.index,
      result.destination.index
    )

    setSurvey(items)
  }

  return (
    <div css={mainCss}>
      <div className="fields">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {survey.map((field, index) => (
                  <Draggable
                    key={field.id}
                    draggableId={field.id}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        className="field"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <Field
                          {...field}
                          key={field.id}
                          drop={() => dropField(field.id)}
                          copy={() => copyField(field.id)}
                          update={update(field.id)}
                          disable={!(survey.length > 1)}
                          submitted={submitted}
                          dragHandleProps={{ ...provided.dragHandleProps }}
                          index={index}
                          isTemplate={isTemplate}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <div css={buttonsCss}>
        <NextField
          label={
            isSmall ? (
              <Fragment>
                <span>+</span> Add question
              </Fragment>
            ) : (
              <Fragment>
                <span>+</span> Add another question
              </Fragment>
            )
          }
          addField={addField}
        />
        {hasChanges && (
          <Button
            primary
            className="save"
            onClick={() => {
              setSubmitted(true)
              if (!checkSurveyInvalid()) {
                setIsOpen(true)
              }
            }}
            data-cy="submit-survey"
          >
            <span>{isSmall ? 'Save' : 'Save this survey'}</span>
          </Button>
        )}
        {isOpen && (
          <SubmitModal
            survey={survey}
            setIsOpen={setIsOpen}
            setSubmitted={setSubmitted}
          />
        )}
      </div>
    </div>
  )
}

export default Sandbox
